import type { ActionFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import invariant from 'tiny-invariant';
import { BOARD_TERMS } from '~/config/mood-board-terminology';
import type {
    RemoveMoodBoardPinsByPageMutation,
    RemoveMoodBoardPinsByPageMutationVariables,
} from 'generated/graphql';
import { RemoveMoodBoardPinsByPageDocument } from 'generated/graphql';

export const REMOVE_INSPIRATION_ITEMS_BY_PAGE_API_ROUTE =
    '/actions/remove-items-from-mood-board-by-page';

const DEFAULT_FAILED_MESSAGE = `Failed to remove ${BOARD_TERMS.singular.toLowerCase()} items.`;

/**
 * This mutation is similar to REMOVE_BUILD_HUB_INSPIRATION_ITEMS_API_ROUTE
 * but is deleting by library item id instead of the pin's ID.
 * This is needed for use with the pin saving/removing modal as the useHandleFollow works with library item ID not the pin's ID
 */
export const action: ActionFunction = async ({ request, context }) => {
    const data = await request.formData();

    invariant(data.get('boardId'), 'Board id required.');
    invariant(data.get('pageIds[]'), 'Page ids required for remove.');

    const variables: RemoveMoodBoardPinsByPageMutationVariables = {
        BoardID: data.get('boardId') as string,
        LibraryItemIDs: data.getAll('pageIds[]').map(String),
    };

    try {
        const { data: result } = await context.client.mutate<
            RemoveMoodBoardPinsByPageMutation,
            RemoveMoodBoardPinsByPageMutationVariables
        >({
            mutation: RemoveMoodBoardPinsByPageDocument,
            variables,
        });

        const validResult = result?.moodBoardPinsRemoveByPage;
        if (!validResult) {
            return json({
                buildHubUpsert: {
                    __typename: 'ServerError',
                    Message: DEFAULT_FAILED_MESSAGE,
                },
            });
        }

        return json(result);
    } catch (error) {
        return json({
            buildHubUpsert: {
                __typename: 'ServerError',
                Message:
                    error instanceof Error
                        ? error.message
                        : DEFAULT_FAILED_MESSAGE,
            },
        });
    }
};
